/**
 * Utility to validate email addresses for Entra ID (Azure AD) work/school accounts
 * Blocks common consumer email domains that should not be used for admin access
 */

// List of consumer email domains that are NOT allowed for admin accounts
const BLOCKED_CONSUMER_DOMAINS = [
  // Google domains
  'gmail.com',
  'googlemail.com',
  
  // Microsoft personal domains
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',
  
  // Yahoo domains
  'yahoo.com',
  'ymail.com',
  'rocketmail.com',
  
  // Other common consumer domains
  'aol.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'protonmail.com',
  'mail.com',
  'zoho.com',
  'gmx.com',
  'yandex.com'
];

/**
 * Validates if an email address is suitable for Entra ID (Azure AD) work/school accounts
 * @param {string} email - The email address to validate
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export const validateEntraEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return { isValid: false, error: 'Email is required' };
  }

  const trimmedEmail = email.trim().toLowerCase();

  // Basic email format validation
  if (!trimmedEmail.includes('@')) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }

  const parts = trimmedEmail.split('@');
  if (parts.length !== 2 || !parts[0] || !parts[1]) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }

  const domain = parts[1];

  // Check against blocked consumer domains
  if (BLOCKED_CONSUMER_DOMAINS.includes(domain)) {
    return { 
      isValid: false, 
      error: `Personal email accounts (${domain}) cannot be used. Please use a Microsoft work or school account (e.g., user@company.com)` 
    };
  }

  // Check for .onmicrosoft.com (valid Azure AD domain)
  if (domain.endsWith('.onmicrosoft.com')) {
    return { isValid: true, error: null };
  }

  // For custom domains, we can't definitively know if it's an Entra ID account
  // but we've at least blocked the obvious consumer domains
  return { isValid: true, error: null };
};

/**
 * Gets a user-friendly message explaining email requirements
 * @returns {string}
 */
export const getEntraEmailRequirements = () => {
  return 'Only Microsoft work or school accounts (Azure AD/Entra ID) can be used. Personal email accounts like Gmail, Outlook.com, Yahoo, etc. are not supported.';
};
